import React, { useState, useMemo } from 'react';
import {
  useListTechProjectsQuery,
  useCreateTechProjectMutation,
  useUpdateTechProjectStatusMutation,
  useDeleteTechProjectMutation,
  useListTechLanesQuery,
  useLoadCompanyDirectoryQuery,
} from '../app/api';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
};

const STATUS_COLORS = {
  'Backlog':     '#9d9fa1',
  'In Progress': brand.tealBlue,
  'Review':      brand.goldenrod,
  'Done':        '#2e7d32',
};

const sectionCard = (title, icon, children) => (
  <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', overflow: 'hidden', marginBottom: '1.5rem' }}>
    <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <i className={`bi bi-${icon}`} style={{ color: brand.goldenrod }} />
      <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>{title}</h5>
    </div>
    <div style={{ padding: '1.25rem' }}>{children}</div>
  </div>
);

const EMPTY_PROJECT = {
  name: '',
  status: 'Backlog',
  owner: '',
  priority: 'Medium',
  description: '',
  lane_id: '',
  estimated_start_date: '',
  estimated_end_date: '',
};

const formatDate = (iso) => {
  if (!iso) return '';
  // Server returns ISO strings (Date column -> 'YYYY-MM-DD'). Display compact.
  return iso.length >= 10 ? iso.slice(0, 10) : iso;
};

export default function Tech() {
  const { data: projects = [], isLoading, error } = useListTechProjectsQuery();
  const { data: lanes = [] } = useListTechLanesQuery();
  const { data: directory = [] } = useLoadCompanyDirectoryQuery();
  const [createProject, { isLoading: isCreating }] = useCreateTechProjectMutation();
  const [updateStatusMutation] = useUpdateTechProjectStatusMutation();
  const [deleteProject] = useDeleteTechProjectMutation();

  const [showNewForm, setShowNewForm] = useState(false);
  const [newProject, setNewProject] = useState(EMPTY_PROJECT);
  const [actionError, setActionError] = useState(null);

  // Only directory rows flagged "Enable Project Owner" in HR > Manage Directory
  // appear in the owner picker. Keeps the list to the small set of folks the
  // Tech team actually assigns work to.
  const ownerOptions = useMemo(
    () => directory
      .filter(d => d.final_email && d.can_own_projects)
      .map(d => ({
        email: d.final_email,
        label: `${d.first_name || ''} ${d.last_name || ''}`.trim() || d.final_email,
      })),
    [directory]
  );

  const inputStyle = {
    width: '100%', padding: '8px 12px', border: `1px solid ${brand.softGray}`,
    borderRadius: '6px', fontSize: '0.875rem', color: brand.deepBlue, outline: 'none', fontFamily: 'inherit',
  };
  const btnPrimary = {
    background: brand.deepBlue, color: 'white', border: 'none', borderRadius: '6px',
    padding: '8px 16px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
  };

  const addProject = async () => {
    setActionError(null);
    if (!newProject.name.trim()) return;
    try {
      await createProject({
        name: newProject.name.trim(),
        status: newProject.status,
        owner: newProject.owner || null,
        priority: newProject.priority,
        description: newProject.description || null,
        lane_id: newProject.lane_id === '' ? null : Number(newProject.lane_id),
        estimated_start_date: newProject.estimated_start_date || null,
        estimated_end_date: newProject.estimated_end_date || null,
      }).unwrap();
      setNewProject(EMPTY_PROJECT);
      setShowNewForm(false);
    } catch (err) {
      setActionError(err?.data?.detail || 'Could not create project.');
    }
  };

  const updateStatus = async (id, status) => {
    setActionError(null);
    try {
      await updateStatusMutation({ id, status }).unwrap();
    } catch (err) {
      setActionError(err?.data?.detail || 'Could not update status.');
    }
  };

  const removeProject = async (project) => {
    setActionError(null);
    const ok = window.confirm(`Delete project "${project.name}"? This cannot be undone.`);
    if (!ok) return;
    try {
      await deleteProject({ id: project.id }).unwrap();
    } catch (err) {
      setActionError(err?.data?.detail || 'Could not delete project.');
    }
  };

  const statusCounts = Object.fromEntries(
    Object.keys(STATUS_COLORS).map(s => [s, projects.filter(p => p.status === s).length])
  );

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>

      <div style={{ marginBottom: '1.5rem' }}>
        <h4 style={{ color: brand.deepBlue, fontWeight: 800, margin: 0 }}>Tech</h4>
        <p style={{ color: brand.charcoal, margin: 0, fontSize: '0.875rem' }}>Project planning and technology initiatives</p>
      </div>

      {/* Status summary */}
      <div className="row g-3 mb-4">
        {Object.entries(STATUS_COLORS).map(([status, color]) => (
          <div key={status} className="col-6 col-md-3">
            <div style={{ background: 'white', borderRadius: '10px', padding: '1rem', boxShadow: '0 2px 8px rgba(10,41,71,0.08)', borderTop: `3px solid ${color}` }}>
              <div style={{ fontSize: '1.75rem', fontWeight: 800, color: brand.deepBlue }}>{statusCounts[status]}</div>
              <div style={{ fontSize: '0.8rem', color: brand.charcoal, fontWeight: 600 }}>{status}</div>
            </div>
          </div>
        ))}
      </div>

      {actionError && (
        <div style={{ background: '#fdecea', color: '#b00020', borderRadius: '8px', padding: '0.6rem 0.9rem', marginBottom: '1rem', fontSize: '0.875rem' }}>
          {actionError}
        </div>
      )}
      {error && (
        <div style={{ background: '#fdecea', color: '#b00020', borderRadius: '8px', padding: '0.6rem 0.9rem', marginBottom: '1rem', fontSize: '0.875rem' }}>
          Could not load projects.
        </div>
      )}

      {/* Project list */}
      {sectionCard('Projects', 'kanban',
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
            <button style={btnPrimary} onClick={() => setShowNewForm(v => !v)}>
              <i className="bi bi-plus me-1" />{showNewForm ? 'Cancel' : 'New Project'}
            </button>
          </div>

          {showNewForm && (
            <div style={{ background: brand.softGray, borderRadius: '8px', padding: '1rem', marginBottom: '1rem', display: 'grid', gap: '0.5rem' }}>
              <input style={inputStyle} placeholder="Project name" value={newProject.name}
                onChange={e => setNewProject(p => ({ ...p, name: e.target.value }))} />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
                <select style={inputStyle} value={newProject.status}
                  onChange={e => setNewProject(p => ({ ...p, status: e.target.value }))}>
                  {Object.keys(STATUS_COLORS).map(s => <option key={s}>{s}</option>)}
                </select>
                <select style={inputStyle} value={newProject.priority}
                  onChange={e => setNewProject(p => ({ ...p, priority: e.target.value }))}>
                  {['High', 'Medium', 'Low'].map(s => <option key={s}>{s}</option>)}
                </select>
                <select style={inputStyle} value={newProject.lane_id}
                  onChange={e => setNewProject(p => ({ ...p, lane_id: e.target.value }))}>
                  <option value="">— Lane —</option>
                  {lanes.map(lane => (
                    <option key={lane.id} value={lane.id}>{lane.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
                <select style={inputStyle} value={newProject.owner}
                  onChange={e => setNewProject(p => ({ ...p, owner: e.target.value }))}>
                  <option value="">— Owner —</option>
                  {ownerOptions.map(opt => (
                    <option key={opt.email} value={opt.email}>{opt.label} ({opt.email})</option>
                  ))}
                </select>
                <input style={inputStyle} type="date"
                  value={newProject.estimated_start_date}
                  onChange={e => setNewProject(p => ({ ...p, estimated_start_date: e.target.value }))}
                  placeholder="Start date" />
                <input style={inputStyle} type="date"
                  value={newProject.estimated_end_date}
                  onChange={e => setNewProject(p => ({ ...p, estimated_end_date: e.target.value }))}
                  placeholder="End date" />
              </div>

              <textarea style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }} placeholder="Description"
                value={newProject.description} onChange={e => setNewProject(p => ({ ...p, description: e.target.value }))} />
              <div>
                <button style={btnPrimary} onClick={addProject} disabled={isCreating || !newProject.name.trim()}>
                  {isCreating ? 'Adding…' : 'Add Project'}
                </button>
              </div>
            </div>
          )}

          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
              <div className="spinner-border" style={{ color: brand.tealBlue }} />
            </div>
          ) : projects.length === 0 ? (
            <div style={{ textAlign: 'center', color: brand.charcoal, padding: '2rem', fontSize: '0.9rem' }}>
              No projects yet. Click <strong>New Project</strong> to add one.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${brand.softGray}` }}>
                  {['Project', 'Lane', 'Status', 'Priority', 'Owner', 'Start', 'End', 'Description', ''].map(h => (
                    <th key={h} style={{ padding: '0.6rem 0.75rem', color: brand.charcoal, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {projects.map(proj => (
                  <tr key={proj.id} style={{ borderBottom: `1px solid ${brand.softGray}` }}>
                    <td style={{ padding: '0.75rem', fontWeight: 600, color: brand.deepBlue }}>{proj.name}</td>
                    <td style={{ padding: '0.75rem', color: brand.charcoal, fontSize: '0.875rem' }}>{proj.lane_name || ''}</td>
                    <td style={{ padding: '0.75rem' }}>
                      <select value={proj.status} onChange={e => updateStatus(proj.id, e.target.value)}
                        style={{ background: 'transparent', border: 'none', fontWeight: 700, color: STATUS_COLORS[proj.status] || brand.charcoal, fontSize: '0.8rem', cursor: 'pointer', outline: 'none' }}>
                        {Object.keys(STATUS_COLORS).map(s => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, padding: '3px 8px', borderRadius: '4px',
                        background: proj.priority === 'High' ? 'rgba(10,41,71,0.1)' : proj.priority === 'Medium' ? 'rgba(0,105,166,0.1)' : 'rgba(88,88,91,0.1)',
                        color: proj.priority === 'High' ? brand.deepBlue : proj.priority === 'Medium' ? brand.tealBlue : brand.charcoal }}>
                        {proj.priority}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem', color: brand.charcoal, fontSize: '0.875rem' }}>{proj.owner || ''}</td>
                    <td style={{ padding: '0.75rem', color: brand.charcoal, fontSize: '0.875rem' }}>{formatDate(proj.estimated_start_date)}</td>
                    <td style={{ padding: '0.75rem', color: brand.charcoal, fontSize: '0.875rem' }}>{formatDate(proj.estimated_end_date)}</td>
                    <td style={{ padding: '0.75rem', color: brand.charcoal, fontSize: '0.875rem' }}>{proj.description || ''}</td>
                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                      <button
                        title="Delete project"
                        onClick={() => removeProject(proj)}
                        style={{ background: 'transparent', border: 'none', color: '#b00020', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px' }}
                      >
                        <i className="bi bi-trash" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
